import * as React from "react"

import {
  FormFieldLabel,
  type FormFieldProps,
} from "@/components/forms/FormField"
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
} from "@/components/ui/field"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { cn } from "cn"

export type FormRadioProps = Omit<FormFieldProps, "label"> &
  Omit<
    React.ComponentProps<typeof RadioGroupItem>,
    "className" | "children"
  > & {
    label: React.ReactNode
    inputClassName?: string
  }

/**
 * Labeled radio option for use inside {@link FormRadioGroup}.
 *
 * Layout matches {@link FormCheckbox}: control beside label, with optional
 * description/error under the label via `FieldContent`.
 *
 * Do not nest another `RadioGroup` — the parent {@link FormRadioGroup} owns
 * selection state.
 */
export function FormRadio({
  label,
  required,
  description,
  error,
  fieldClassName,
  inputClassName,
  id,
  value,
  disabled,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedByProp,
  ...radioProps
}: FormRadioProps) {
  const generatedId = React.useId()
  const radioId = id ?? generatedId
  const invalid =
    Boolean(error) ||
    (ariaInvalid != null && ariaInvalid !== false && ariaInvalid !== "false")

  const descriptionId = description ? `${radioId}-description` : undefined
  const errorId = error ? `${radioId}-error` : undefined
  const ariaDescribedBy =
    [descriptionId, errorId, ariaDescribedByProp].filter(Boolean).join(" ") ||
    undefined

  return (
    <Field
      orientation="horizontal"
      className={cn(fieldClassName)}
      data-invalid={invalid || undefined}
      data-disabled={disabled || undefined}
    >
      <RadioGroupItem
        {...radioProps}
        id={radioId}
        value={value}
        disabled={disabled}
        aria-invalid={ariaInvalid ?? (error ? true : undefined)}
        aria-describedby={ariaDescribedBy}
        className={inputClassName}
      />
      <FieldContent>
        <FormFieldLabel htmlFor={radioId} label={label} required={required} />
        {description ? (
          <FieldDescription id={descriptionId}>{description}</FieldDescription>
        ) : null}
        {error ? <FieldError id={errorId}>{error}</FieldError> : null}
      </FieldContent>
    </Field>
  )
}

export type FormRadioGroupProps = Omit<FormFieldProps, "label"> & {
  label?: React.ReactNode
  value?: string | null
  onChange?: (value: string | null) => void
  disabled?: boolean
  invalid?: boolean
  groupClassName?: string
  children: React.ReactNode
}

/**
 * Mutually exclusive radio group with optional title, description, and error.
 *
 * Owns selection via `value` / `onChange`. Compose options with {@link FormRadio}.
 *
 * @example
 * ```tsx
 * <FormRadioGroup
 *   label="Campaign Type"
 *   value={campaignType}
 *   onChange={setCampaignType}
 * >
 *   <FormRadio value="Song" label="Song Promos" />
 *   <FormRadio value="Brand" label="Brand Promos" />
 * </FormRadioGroup>
 * ```
 */
export function FormRadioGroup({
  label,
  required,
  description,
  error,
  fieldClassName,
  groupClassName,
  value,
  onChange,
  disabled,
  invalid,
  children,
}: FormRadioGroupProps) {
  const generatedId = React.useId()
  const descriptionId = description ? `${generatedId}-description` : undefined
  const errorId = error ? `${generatedId}-error` : undefined
  const ariaDescribedBy =
    [descriptionId, errorId].filter(Boolean).join(" ") || undefined

  return (
    <Field
      className={cn("gap-1", fieldClassName)}
      data-invalid={invalid || Boolean(error) || undefined}
      data-disabled={disabled || undefined}
    >
      {label ? <FormFieldLabel label={label} required={required} /> : null}
      <RadioGroup
        value={value ?? undefined}
        onValueChange={(next) => onChange?.(next ?? null)}
        disabled={disabled}
        className={cn("gap-3", groupClassName)}
        aria-describedby={ariaDescribedBy}
        aria-invalid={invalid || Boolean(error) || undefined}
      >
        {children}
      </RadioGroup>
      {description ? (
        <FieldDescription id={descriptionId}>{description}</FieldDescription>
      ) : null}
      {error ? <FieldError id={errorId}>{error}</FieldError> : null}
    </Field>
  )
}
